/** Application navigation and permission bindings; presentation lives in IslandSidebar. */
import {
  Blocks,
  House,
  MessageSquare,
  FolderOpen,
  ChartColumn,
  Search,
  Plug,
  FileText,
  ClipboardList,
  Bell,
  BookOpen,
  Brain,
  BrainCircuit,
  ScrollText,
  Radio,
  Library,
  ShieldCheck,
  Send,
  Inbox,
  Lightbulb,
  Newspaper,
  FingerprintPattern,
  RotateCcwClock,
  Scale,
  Gauge,
  CircuitBoard,
  Settings,
} from 'lucide-react'
import type { ComponentType } from 'react'
import type { GrantTier } from '@/types/auth'

export interface NavGroupChild {
  path: string
  label: string
  icon: ComponentType<{ className?: string }>
  /** FeatureArea value string (e.g. 'todos') — must match a backend FeatureArea.value. */
  featureArea: string
  /** Minimum capability rank required to show this child. */
  minRank: GrantTier
}

export interface NavLeaf {
  kind: 'leaf'
  path: string
  label: string
  icon: ComponentType<{ className?: string }>
  /** FeatureArea value string — must match a backend FeatureArea.value (D-2). */
  featureArea: string
  /** Minimum capability rank required to show this leaf. */
  minRank: GrantTier
}

export interface NavGroup {
  kind: 'group'
  label: string
  icon: ComponentType<{ className?: string }>
  children: NavGroupChild[]
}

export type NavItem = (NavLeaf | NavGroup) & {
  /** Extra spacing separates primary links, the memory model, and supporting tools. */
  sectionStart?: boolean
}

// Bindings are enforcement-aligned (D-2): each leaf's featureArea matches the
// FeatureArea the route gate enforces for that page, so sidebar visibility is a
// true projection of access. 'core' is always-visible (D-9); 'auth' and
// 'telemetry-policies' require admin because those routes enforce at admin rank.
// '/' binds 'core' (PRD-0271 D-1) — the Alveary landing conversation is
// always-visible, like /cc-support and /memory-model; the moved dashboard at
// '/dashboard' keeps the 'dashboard' binding the old '/' leaf enforced.
// '/collectors' binds to 'releases' (not 'setup') — collectors data routes are
// enforced under 'releases'; 'setup' backs only public install routes.
// '/memory-model' binds to 'core' — a static concept page with no API surface,
// always-visible like /cc-support.
// Groups without accessible children are suppressed. Exported for the binding drift guard.
export const ALL_NAV_ITEMS: NavItem[] = [
  {
    kind: 'leaf',
    path: '/',
    label: 'Home',
    icon: MessageSquare,
    featureArea: 'core',
    minRank: 'read',
  },
  {
    kind: 'leaf',
    path: '/projects',
    label: 'Projects',
    icon: FolderOpen,
    featureArea: 'projects',
    minRank: 'read',
  },
  // The memory cluster reads top-down as concept → library → semantic
  // recall → desk. The grouped subsystem surfaces keep their related tools at
  // the seam where users expect to find them.
  {
    kind: 'leaf',
    sectionStart: true,
    path: '/memory-model',
    label: 'Memory Model',
    icon: BookOpen,
    featureArea: 'core',
    minRank: 'read',
  },
  {
    kind: 'leaf',
    path: '/procedural-memory',
    label: 'Procedural Memory',
    icon: FileText,
    featureArea: 'procedural-memory',
    minRank: 'read',
  },
  {
    kind: 'leaf',
    path: '/knowledge-base',
    label: 'Knowledge Base',
    icon: BookOpen,
    featureArea: 'knowledge-base',
    minRank: 'read',
  },
  {
    kind: 'group',
    label: 'Semantic Memory',
    icon: Brain,
    children: [
      {
        path: '/search',
        label: 'Search',
        icon: Search,
        featureArea: 'semantic-memory',
        minRank: 'read',
      },
      {
        path: '/subjects',
        label: 'Subjects',
        icon: FingerprintPattern,
        featureArea: 'semantic-memory',
        minRank: 'read',
      },
      {
        path: '/episodes',
        label: 'Episodes',
        icon: RotateCcwClock,
        featureArea: 'semantic-memory',
        minRank: 'read',
      },
      // installed lucide-react (0.300.0) predates NotebookText; Newspaper is
      // the designated fallback (PRD-0210 D-14).
      {
        path: '/session-summaries',
        label: 'Session Summaries',
        icon: Newspaper,
        featureArea: 'summarization',
        minRank: 'read',
      },
      {
        path: '/claims',
        label: 'Claims',
        icon: Scale,
        featureArea: 'semantic-memory',
        minRank: 'read',
      },
    ],
  },
  {
    kind: 'group',
    label: 'Working Memory',
    icon: BrainCircuit,
    children: [
      {
        path: '/todos',
        label: 'ToDo List',
        icon: ClipboardList,
        featureArea: 'todos',
        minRank: 'read',
      },
      {
        path: '/ideas',
        label: 'Ideas',
        icon: Lightbulb,
        featureArea: 'ideas',
        minRank: 'read',
      },
      {
        path: '/reminders',
        label: 'Reminders',
        icon: Bell,
        featureArea: 'reminders',
        minRank: 'read',
      },
      {
        path: '/prds',
        label: 'PRDs',
        icon: ScrollText,
        featureArea: 'prds',
        minRank: 'read',
      },
      {
        path: '/prompt-library',
        label: 'Prompt Library',
        icon: Library,
        featureArea: 'prompts',
        minRank: 'read',
      },
    ],
  },
  {
    kind: 'group',
    sectionStart: true,
    label: 'Operations',
    icon: Send,
    children: [
      {
        path: '/intake',
        label: 'Customer Intake',
        icon: Inbox,
        featureArea: 'intake',
        minRank: 'read',
      },
      {
        path: '/dispatch',
        label: 'Dispatch',
        icon: Send,
        featureArea: 'dispatch',
        minRank: 'read',
      },
    ],
  },
  {
    kind: 'group',
    label: 'Monitoring',
    icon: ChartColumn,
    children: [
      {
        path: '/dashboard',
        label: 'Dashboard',
        icon: House,
        featureArea: 'dashboard',
        minRank: 'read',
      },
      {
        path: '/analytics',
        label: 'Analytics',
        icon: ChartColumn,
        featureArea: 'analytics',
        minRank: 'read',
      },
      {
        path: '/usage',
        label: 'Usage',
        icon: Gauge,
        featureArea: 'usage',
        minRank: 'read',
      },
    ],
  },
  {
    kind: 'group',
    label: 'Platform',
    icon: Settings,
    children: [
      {
        path: '/collectors',
        label: 'Collectors',
        icon: Radio,
        featureArea: 'releases',
        minRank: 'read',
      },
      {
        path: '/model-capabilities',
        label: 'Model Capabilities',
        icon: CircuitBoard,
        featureArea: 'model-capabilities',
        minRank: 'read',
      },
      {
        path: '/integrations',
        label: 'Integrations',
        icon: Blocks,
        featureArea: 'integrations',
        minRank: 'read',
      },
      {
        path: '/access-control',
        label: 'Access Control',
        icon: ShieldCheck,
        featureArea: 'auth',
        minRank: 'admin',
      },
      {
        path: '/telemetry-policies',
        label: 'Telemetry Policies',
        icon: ShieldCheck,
        featureArea: 'telemetry-policies',
        minRank: 'admin',
      },
    ],
  },
  {
    kind: 'leaf',
    path: '/cc-support',
    label: 'Setup & Help',
    icon: Plug,
    featureArea: 'core',
    minRank: 'read',
  },
]

/** Segment-bound matching avoids activating /projects for /projects-archive. */
export function isNavigationActive(pathname: string, path: string): boolean {
  return pathname === path || (path !== '/' && pathname.startsWith(path + '/'))
}

/**
 * The groups that own a route. A rail reveal restores exactly this set, so the
 * menu answers "where am I?" without carrying stale disclosures into the view.
 * Capability filtering is not applied: a group with no visible children is not
 * rendered, so an entry for it is inert.
 */
export function activeGroupLabels(pathname: string): string[] {
  return ALL_NAV_ITEMS.filter(
    (item): item is NavItem & NavGroup => item.kind === 'group'
  )
    .filter((group) =>
      group.children.some((child) => isNavigationActive(pathname, child.path))
    )
    .map((group) => group.label)
}
